import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Text,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import { useAppStore } from '@/stores/appStore';
import { useProviderLocations, azureResourceKeys } from '@/hooks/useAzureResources';
import { useTelemetry } from '@/hooks/useTelemetry';
import { setLogicEndpoint } from '@/services/endpoints';
import { getProjectLocation } from '@/utils/system';
import { SearchableCombobox } from '@/components/shared';

const ALL_REGIONS = '__all__';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap' as const,
  },
  combobox: {
    minWidth: '160px',
    maxWidth: '280px',
  },
});

interface RegionFilterProps {
  /** Optional class for the root element */
  className?: string;
  /** When true, only updates the store without navigating. Use on pages that reactively filter. */
  disableNavigation?: boolean;
  /**
   * Controlled mode: when provided, the filter uses this value and calls onChange instead of
   * modifying the global appStore/endpoint. Use on pages that manage their own filter state
   * (e.g., DashboardHomePage with multi-region fetching).
   */
  value?: string | null;
  /** Called when the user picks a region in controlled mode. Receives region name or null for "All". */
  onChange?: (region: string | null) => void;
}

/**
 * Region/location filter for the project list page.
 * Extracted from ContextChip for use as a standalone filter control.
 * Shows "All regions" when no region is selected.
 */
export const RegionFilter: React.FC<RegionFilterProps> = ({ className, disableNavigation, value, onChange }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isControlled = onChange !== undefined;

  const { selectedLocation: storeLocation, setSelectedLocation } = useAppStore();
  const activeLocation = isControlled ? (value ?? null) : storeLocation;
  const { data: locations, isLoading: locationsLoading } = useProviderLocations();
  const { trackEvent } = useTelemetry();

  // The "All regions" sentinel is a synthetic option so the combobox can
  // represent "no filter" as a selectable value. `SearchableCombobox`
  // filters it like any other option, which matches what users expect when
  // they type.
  const options = useMemo(() => {
    const base = [{ value: ALL_REGIONS, text: 'All regions' }];
    if (!locations) return base;
    return [
      ...base,
      ...locations.map((l) => ({
        value: l.name,
        text: l.displayName,
        searchTerms: [l.name],
      })),
    ];
  }, [locations]);

  const handleLocationSelect = useCallback(
    (next: string) => {
      if (next === ALL_REGIONS) {
        trackEvent('navigation.region.cleared');
        if (isControlled) {
          onChange(null);
        } else {
          setSelectedLocation(null);
          setLogicEndpoint(null);
          queryClient.resetQueries({ queryKey: azureResourceKeys.projectsPrefix() });
          if (!disableNavigation) navigate('/?tab=projects');
        }
        return;
      }

      trackEvent('navigation.region.changed', { location: next });
      if (isControlled) {
        onChange(next);
      } else {
        setSelectedLocation(next);
        setLogicEndpoint(getProjectLocation(next));
        queryClient.resetQueries({ queryKey: azureResourceKeys.projectsPrefix() });
        if (!disableNavigation) navigate(`/?tab=projects&region=${encodeURIComponent(next)}`);
      }
    },
    [trackEvent, setSelectedLocation, queryClient, navigate, disableNavigation, isControlled, onChange],
  );

  return (
    <div className={`${styles.root}${className ? ` ${className}` : ''}`}>
      <Text className={styles.label}>Region:</Text>
      <SearchableCombobox
        className={styles.combobox}
        options={options}
        value={activeLocation ?? ALL_REGIONS}
        onChange={handleLocationSelect}
        disabled={locationsLoading}
        size="small"
        placeholder={locationsLoading ? 'Loading regions...' : 'All regions'}
        aria-label="Filter by region"
        noMatchMessage="No regions found"
      />
    </div>
  );
};
